import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthHero from '../../../shared/auth/components/AuthHero';
import { FormErrors } from '../../../shared/auth/types';
import { AUTH_ERROR_MESSAGES } from '../../../shared/auth/constants';
import { ENV } from '../../../config/env';
import crypto from 'crypto-js';

interface RecruiterJoinData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    jobTitle: string;
    joinCode: string;
    agreeToTerms: boolean;
}

/**
 * RecruiterJoinTeam Page
 * Protected by URL Token Validation - only accessible with valid invitation code/email in URL
 * Direct URL access without valid token redirects to login page
 */
const RecruiterJoinTeam: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get invitation token from URL (required for access)
    const invitationCode = searchParams.get('code') || searchParams.get('invitation_id') || '';
    const invitedEmail = searchParams.get('email') || '';

    // URL Token Validation - redirect if no invitation code provided
    useEffect(() => {
        if (!invitationCode) {
            console.warn('🚫 RecruiterJoinTeam: No invitation code provided, redirecting to login');
            navigate('/auth/login', { replace: true });
        }
    }, [invitationCode, navigate]);

    const [joinData, setJoinData] = useState<RecruiterJoinData>({
        firstName: '',
        lastName: '',
        email: invitedEmail,  // Pre-fill email from URL
        password: '',
        jobTitle: '',
        joinCode: invitationCode,  // Pre-fill join code from URL
        agreeToTerms: false,
    });

    const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Don't render if no invitation code (will redirect)
    if (!invitationCode) {
        return null;
    }

    const handleInputChange = (field: keyof RecruiterJoinData, value: string | boolean) => {
        setJoinData(prev => ({
            ...prev,
            [field]: value,
        }));

        // Clear field error when user starts typing
        if (fieldErrors[field as keyof FormErrors]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };

    const validateForm = (): FormErrors => {
        const errors: FormErrors = {};

        if (!joinData.firstName.trim()) {
            errors.firstName = AUTH_ERROR_MESSAGES.FIRST_NAME_REQUIRED;
        }

        if (!joinData.lastName.trim()) {
            errors.lastName = AUTH_ERROR_MESSAGES.LAST_NAME_REQUIRED;
        }

        if (!joinData.email.trim()) {
            errors.email = AUTH_ERROR_MESSAGES.EMAIL_REQUIRED;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(joinData.email)) {
            errors.email = AUTH_ERROR_MESSAGES.INVALID_EMAIL;
        }

        if (!joinData.password) {
            errors.password = AUTH_ERROR_MESSAGES.PASSWORD_REQUIRED;
        } else if (joinData.password.length < 8) {
            errors.password = AUTH_ERROR_MESSAGES.PASSWORD_TOO_SHORT;
        }

        if (!joinData.jobTitle.trim()) {
            errors.general = 'Job title is required';
        }

        if (!joinData.joinCode.trim()) {
            errors.general = 'Join code is required';
        }

        if (!joinData.agreeToTerms) {
            errors.agreeToTerms = AUTH_ERROR_MESSAGES.TERMS_REQUIRED;
        }

        return errors;
    };

    // Password encryption utility (same as auth service)
    const hashAndEncryptPassword = async (password: string): Promise<string> => {
        try {
            // Step 1: Hash the password using SHA-256
            const hash = crypto.SHA256(password).toString(crypto.enc.Hex);

            // Step 2: Take only first 32 characters of hash
            const shortHash = hash.substring(0, 32);

            // Step 3: Create a compliant string
            const compliant = `Aa1!${shortHash}Z@`;

            // Step 4: Base64 encode
            const encrypted = btoa(compliant);

            // Step 5: Ensure it's under 72 characters
            if (encrypted.length > 72) {
                return encrypted.substring(0, 72);
            }

            return encrypted;
        } catch (error: any) {
            console.error('🚨 Password encryption failed:', error);
            throw new Error('Password encryption failed');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        const errors = validateForm();
        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        setIsLoading(true);

        try {
            // Send plain password - Supabase handles password hashing securely
            // Call Go backend for recruiter signup via invitation
            const response = await fetch(`${ENV.API_BASE_URL}/auth/v1/signup/recruiter/${joinData.joinCode}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    firstName: joinData.firstName,
                    lastName: joinData.lastName,
                    email: joinData.email,
                    password: joinData.password,
                    jobTitle: joinData.jobTitle,
                    location: '', // Optional field
                    phone: '' // Optional field
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                // Error response format from Go backend:
                // { error: "ALREADY_EXISTS"|"ERROR_VALIDATION"|..., status: { msg: "...", error: true } }
                const errorCode = data.error || '';
                const errorMsg = data.status?.msg || data.detail || data.message || '';

                if (response.status === 409 || errorCode === 'ALREADY_EXISTS') {
                    setFieldErrors(prev => ({
                        ...prev,
                        email: 'A user with this email already exists. Please log in or use a different email.'
                    }));
                } else if (response.status === 404 || errorMsg.includes('invitation') || errorMsg.includes('expired')) {
                    setFieldErrors(prev => ({
                        ...prev,
                        general: 'Invalid or expired invitation code. Please check your invitation link.'
                    }));
                } else if (response.status === 400 && (errorMsg.includes('email') || errorMsg.includes('match'))) {
                    setFieldErrors(prev => ({
                        ...prev,
                        email: 'This email does not match the invitation.'
                    }));
                } else {
                    setFieldErrors(prev => ({
                        ...prev,
                        general: errorMsg || 'Failed to join team. Please try again.'
                    }));
                }
                return;
            }

            console.log('✅ Recruiter joined successfully:', data);

            // Auto-login: Store tokens and user data
            if (data.access_token && data.user) {
                localStorage.setItem('authToken', data.access_token);
                localStorage.setItem('refreshToken', data.refresh_token);
                localStorage.setItem('authUser', JSON.stringify(data.user));

                console.log('✅ Auto-login successful, redirecting to dashboard...');

                // Redirect to dashboard
                navigate('/companies/dashboard', {
                    replace: true
                });
            } else {
                // Fallback: navigate to welcome page if no tokens (shouldn't happen)
                navigate('/auth/recruiter/welcome', {
                    state: {
                        userName: joinData.firstName,
                        companyName: data.company_name || 'the team',
                        needsVerification: true
                    }
                });
            }
        } catch (err: any) {
            console.error('❌ Recruiter join failed:', err);
            // Handle network errors
            setFieldErrors(prev => ({
                ...prev,
                general: 'Network error. Please check your connection and try again.'
            }));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Form */}
            <div className="w-full lg:flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-[597px]">
                    {/* Logo */}
                    <div className="mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            onClick={() => navigate('/')}
                            className="h-12 w-auto cursor-pointer"
                        />
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '48px',
                            lineHeight: '100%',
                            color: '#015FA6',
                            marginBottom: '8px'
                        }}
                    >
                        Join Your Team on Hush
                    </h1>

                    {/* Subtitle */}
                    <p
                        style={{
                            fontFamily: 'SF Pro',
                            fontWeight: 110,
                            fontSize: '18px',
                            lineHeight: '150%',
                            color: '#015FA6',
                            marginBottom: '32px'
                        }}
                    >
                        Looks like your company already has an account. Enter your invite code to join.
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* First Name & Last Name Row */}
                        <div style={{ display: 'flex', gap: '22px', marginBottom: '24px' }}>
                            {/* First Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: '#015FA6',
                                        display: 'block',
                                        marginBottom: '14px',
                                        letterSpacing: '-0.154px'
                                    }}
                                >
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={joinData.firstName}
                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                    placeholder="Hush"
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: fieldErrors.firstName ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px'
                                    }}
                                    onFocus={(e) => {
                                        if (!fieldErrors.firstName) {
                                            e.target.style.borderColor = '#015FA6';
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!fieldErrors.firstName) {
                                            e.target.style.borderColor = '#CBD5E0';
                                        }
                                    }}
                                />
                                {fieldErrors.firstName && (
                                    <p style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
                                        {fieldErrors.firstName}
                                    </p>
                                )}
                            </div>

                            {/* Last Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: '#015FA6',
                                        display: 'block',
                                        marginBottom: '14px',
                                        letterSpacing: '-0.154px'
                                    }}
                                >
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={joinData.lastName}
                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                    placeholder="example.com"
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: fieldErrors.lastName ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px'
                                    }}
                                    onFocus={(e) => {
                                        if (!fieldErrors.lastName) {
                                            e.target.style.borderColor = '#015FA6';
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!fieldErrors.lastName) {
                                            e.target.style.borderColor = '#CBD5E0';
                                        }
                                    }}
                                />
                                {fieldErrors.lastName && (
                                    <p style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>
                                        {fieldErrors.lastName}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Email Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#015FA6',
                                    display: 'block',
                                    marginBottom: '14px',
                                    letterSpacing: '-0.154px'
                                }}
                            >
                                Email
                            </label>
                            <input
                                type="email"
                                value={joinData.email}
                                onChange={(e) => !invitedEmail && handleInputChange('email', e.target.value)}
                                readOnly={!!invitedEmail}
                                placeholder="example@gmail.com"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: invitedEmail ? '#6B7280' : '#015FA6',
                                    background: invitedEmail ? '#EDF2F7' : '#F7FAFC',
                                    border: fieldErrors.email ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px',
                                    cursor: invitedEmail ? 'not-allowed' : 'text'
                                }}
                                onFocus={(e) => {
                                    if (!fieldErrors.email && !invitedEmail) {
                                        e.target.style.borderColor = '#015FA6';
                                    }
                                }}
                                onBlur={(e) => {
                                    if (!fieldErrors.email) {
                                        e.target.style.borderColor = '#CBD5E0';
                                    }
                                }}
                            />
                            {fieldErrors.email && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '12px',
                                    color: '#EF4444',
                                    marginTop: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    {fieldErrors.email}
                                </p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#015FA6',
                                    display: 'block',
                                    marginBottom: '14px',
                                    letterSpacing: '-0.154px'
                                }}
                            >
                                Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={joinData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    placeholder="@#*%"
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 50px 10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: fieldErrors.password ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                        borderRadius: '12px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px'
                                    }}
                                    onFocus={(e) => {
                                        if (!fieldErrors.password) {
                                            e.target.style.borderColor = '#015FA6';
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!fieldErrors.password) {
                                            e.target.style.borderColor = '#CBD5E0';
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#015FA6',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {showPassword ? (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M1 1l22 22" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth={1.5}/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {fieldErrors.password && (
                                <p style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '12px',
                                    color: '#EF4444',
                                    marginTop: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    {fieldErrors.password}
                                </p>
                            )}
                        </div>

                        {/* Job Title Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#015FA6',
                                    display: 'block',
                                    marginBottom: '14px',
                                    letterSpacing: '-0.154px'
                                }}
                            >
                                Job Title
                            </label>
                            <input
                                type="text"
                                value={joinData.jobTitle}
                                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                                placeholder="Talent Acquisition Lead"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: '1px solid #CBD5E0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                        </div>

                        {/* Join Code Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#015FA6',
                                    display: 'block',
                                    marginBottom: '14px',
                                    letterSpacing: '-0.154px'
                                }}
                            >
                                Join Code
                            </label>
                            <input
                                type="text"
                                value={joinData.joinCode}
                                onChange={(e) => handleInputChange('joinCode', e.target.value)}
                                placeholder="••••••••"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: '1px solid #CBD5E0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                        </div>

                        {/* Terms & Conditions */}
                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={joinData.agreeToTerms}
                                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                                    style={{
                                        accentColor: '#015FA6',
                                        marginTop: '2px',
                                        width: '16px',
                                        height: '16px',
                                        border: '1px solid #CFD9E0',
                                        borderRadius: '4px'
                                    }}
                                />
                                <span
                                    style={{
                                        fontFamily: 'SF Pro',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        lineHeight: '1.4'
                                    }}
                                >
                                    I agree to the
                                    <span style={{ color: '#F59E0B', textDecoration: 'none' }}> Terms of Use</span> and
                                    <span style={{ color: '#F59E0B', textDecoration: 'none' }}> Privacy Policy</span>
                                </span>
                            </label>
                            {fieldErrors.agreeToTerms && (
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '12px',
                                        color: '#EF4444',
                                        marginTop: '4px',
                                        marginLeft: '24px'
                                    }}
                                >
                                    {fieldErrors.agreeToTerms}
                                </p>
                            )}
                        </div>

                        {/* Join Workspace Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                height: '69.82px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '20px',
                                lineHeight: '28px',
                                color: '#F7FAFC',
                                background: isLoading ? '#9CA3AF' : '#015FA6',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#014580';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#015FA6';
                                }
                            }}
                        >
                            {isLoading ? 'Joining workspace...' : 'Join Workspace'}
                        </button>

                        {/* General Error Message */}
                        {fieldErrors.general && (
                            <div
                                style={{
                                    background: '#FEF2F2',
                                    border: '1px solid #FECACA',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginTop: '16px'
                                }}
                            >
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#DC2626',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px'
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
                                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>{fieldErrors.general}</span>
                                </p>
                            </div>
                        )}
                    </form>
                </div>
            </div>

            {/* Right Side - Hero */}
            <div className="hidden lg:block lg:flex-1">
                <AuthHero
                    variant="company"
                    customContent={{
                        title: 'Meet Hush — where talent meets precision.',
                        description: 'We believe finding the right job or candidate shouldn\'t feel like a guessing game. Hush\'s AI interprets your goals, preferences, and potential to deliver matches that make sense — not just noise.'
                    }}
                />
            </div>
        </div>
    );
};

export default RecruiterJoinTeam;
